import { isSoldOut } from '../../core/rules/stock'
import type { Product } from '../../core/types/catalog'
import type { CartAdjustment } from '../../contexts/StoreContext'

/** Eén zin per aanpassing, samengevoegd: "X is tijdelijk uitverkocht…; van Y zijn er nog 2…". */
export function adjustmentText(list: CartAdjustment[]): string {
  const parts = list.map((a) => {
    switch (a.kind) {
      case 'soldOut':
        return `${a.name} is tijdelijk uitverkocht; haal het uit je mand om af te rekenen`
      case 'removed':
        return a.name ? `${a.name} is niet meer leverbaar en is eruit gehaald` : 'een product is niet meer leverbaar en is eruit gehaald'
      case 'reduced':
        return `van ${a.name} ${a.qty === 1 ? 'is er nog 1' : `zijn er nog ${a.qty}`} op voorraad, dus het aantal is aangepast`
    }
  })
  // Alleen uitverkocht: dan is er niets aan de mand veranderd, alleen iets om te weten.
  const lead = list.every((a) => a.kind === 'soldOut') ? 'Let op' : 'Je winkelmand is bijgewerkt'
  return `${lead}: ${parts.join('; ')}.`
}

/** Totalen in de mand altijd met centen (één definitie, in core). */
export { euroCents } from '../../core/format/currency'

/** Zoveel aanbevelingen onder de mand: genoeg voor een keuze, niet meer dan de producten zelf. */
export const MAX_RECOMMENDATIONS = 2

type RecommendationInput = {
  cartProducts: Product[]
  /** Hoeveel er nog mist tot het volgende cadeau; `null` als er geen cadeau meer te halen is. */
  missing: number | null
  products: Product[]
  productsByIds: (ids: string[]) => Product[]
}

/**
 * Eén aanbevelingsblok: eerst producten die bij de mand passen. Is er een cadeau binnen
 * bereik, dan de producten die precies over die drempel helpen (passende eerst, dan goedkoopste).
 * Uitverkochte producten nooit.
 */
export function cartRecommendations({ cartProducts, missing, products, productsByIds }: RecommendationInput): Product[] {
  const inCart = new Set(cartProducts.map((p) => p.id))
  const paired = new Set<string>()
  cartProducts.forEach((p) => p.pairs.forEach((id) => !inCart.has(id) && paired.add(id)))

  const pool =
    missing !== null
      ? products
          .filter((p) => !inCart.has(p.id) && !isSoldOut(p) && p.price >= missing)
          .sort((a, b) => Number(!paired.has(a.id)) - Number(!paired.has(b.id)) || a.price - b.price)
      : productsByIds(Array.from(paired)).filter((p) => !isSoldOut(p))

  return pool.slice(0, MAX_RECOMMENDATIONS)
}
